import { useState, useEffect, useMemo } from 'react';
import { useProject } from '../projects/queries';
import { useTranscriptCount } from '../transcripts/queries';
import { useCodes } from '../codes/queries';
import { useSurveyList } from '../surveys/queries';
import OnboardingStore from 'stores/OnboardingStore'; // Adjust path as needed

/**
 * Hook to determine onboarding state for a project
 * @param {string|number} projectId - The ID of the project
 * @returns {Object} Onboarding state information
 */
export function useOnboarding(projectId) {
  const { data: project, isLoading: projectLoading, isSuccess: projectLoaded } = useProject(projectId);
  const { data: codesData, isLoading: codesLoading } = useCodes(projectId);
  const { data: surveys, isLoading: surveysLoading } = useSurveyList(projectId);
  const { data: transcriptCount = 0, isLoading: transcriptsLoading } = useTranscriptCount(projectId);

  // State for tracking onboarding store changes
  const [gotStarted, setGotStarted] = useState(false);

  // Listen to onboarding store changes
  useEffect(() => {
    const updateGotStarted = () => {
      setGotStarted(OnboardingStore.hasClickedGetStarted());
    };

    // Initial state
    updateGotStarted();

    // Listen for changes
    OnboardingStore.addChangeListener(updateGotStarted);

    // Cleanup
    return () => {
      OnboardingStore.removeChangeListener(updateGotStarted);
    };
  }, []);

  // Extract data with fallbacks
  const codes = codesData?.codes || [];
  const surveysList = surveys || [];

  // Calculate derived states
  const loaded = projectLoaded && !codesLoading && !surveysLoading && !transcriptsLoading;
  const hasCodes = codes.length > 0;

  // Check if any codes have excerpts (count > 0)
  const hasExcerpts = codes.some(code => code && code.count > 0);

  return useMemo(() => ({
    loaded,
    newTranscriptOnboarding: loaded && transcriptCount === 0 && !gotStarted,
    hasNoCodeableData: loaded && transcriptCount === 0 && surveysList.length === 0,
    hasCodes,
    hasExcerpts,
    gotStarted,
    isLoading: projectLoading || codesLoading || surveysLoading || transcriptsLoading,
  }), [loaded, transcriptCount, surveysList.length, gotStarted, hasCodes, hasExcerpts, projectLoading, codesLoading, surveysLoading, transcriptsLoading]);
}